import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@app/classes';
import { ChipItem } from '@app/components/chip-filters/chip-filters-list.component';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { EventType } from '@app/enums/events.type';
import { AnalyticService, AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Day } from '@dashboard/components/upcoming-events/day';
import { EventService } from '@dashboard/services/event.service';
import { CalendarEvent } from '@models/common/calendar-event.model';
import { Department } from '@models/company/department.model';

@Component({
    selector: 'app-upcoming-events-viewer',
    templateUrl: './upcoming-events-viewer.template.html',
    styleUrls: ['./upcoming-events-viewer.style.scss'],
})
export class UpcomingEventsViewerComponent implements OnInit {
    isLoading = false;
    /**
     * These are not all calendar events. They are a mixture of CalendarEvent, birthdays and
     * anniversaries. The latter two are subsets of the CalendarEvent model.
     * TODO: create a new interface which represents a sidebar event and update CalendarEvent
     * to implement it.
     */

    allDepartments: Department[] = [];
    chips: ChipItem<EventType>[] = [
        { displayValue: 'dashboard.all', value: 'all' },
        { displayValue: 'dashboard.away', value: 'away' },
        { displayValue: 'dashboard.birthday', value: 'birthday' },
        { displayValue: 'dashboard.anniversary', value: 'anniversary' },
        { displayValue: 'dashboard.firstDay', value: 'newHire' },
    ];
    days: Day[] = [];
    events: CalendarEvent[] = [];
    firstTwoDays: Day[] = [];
    hasImprovedHomepageFlag = false;
    selectedEvent: ChipItem<EventType> = this.chips[0];
    selectedDepartment: number | null = null;
    next7Days: Day[] = [];
    next7DaysDateGrouping: Day = new Day();

    canAccessCalendar: boolean =
        this.auth.can(Platform.timeOff.permission.calendar) &&
        this.auth.company.modules.some((m) => m.name === Platform.modules.timeOff);

    constructor(
        private auth: AuthService,
        private analyticService: AnalyticService,
        public eventService: EventService,
        private featureService: FeatureService,
        private router: Router
    ) {}

    async ngOnInit(): Promise<void> {
        this.hasImprovedHomepageFlag = await this.featureService.has(FeatureFlag.improvedHomepage);
        this.getDepartments();
        this.eventService.setFeatureflags();
        this.eventService.createDays(this.hasImprovedHomepageFlag);

        // This is done because we want the events list to get all events by default if feature flag is off, to keep old event ui intact (code moved to event service)
        if (!this.hasImprovedHomepageFlag) {
            this.getSelectedEvents(this.chips[0]);
        }
    }

    isPublicView(event): boolean {
        return event.hasOwnProperty('isPublic') && !event.isPublic && !this.auth.isAdmin();
    }

    goToEmployeeProfile(employeeId: number): void {
        this.router.navigate(['/employees', 'directory', employeeId, 'personal']);
    }

    async getSelectedEvents($event: ChipItem<EventType>): Promise<void> {
        this.isLoading = true;
        this.selectedEvent = $event;
        try {
            switch ($event.value) {
                case 'birthday':
                    await this.eventService.loadBirthdayEvents(this.selectedDepartment);
                    this.analyticService.trackEvent(AnalyticEvents.EventsFilterByBirthday);
                    break;

                case 'anniversary':
                    await this.eventService.loadAnniversaryEvents(this.selectedDepartment);
                    this.analyticService.trackEvent(AnalyticEvents.EventsFilterByAnniversary);
                    break;

                case 'away':
                    await this.eventService.loadAwayEvents(this.selectedDepartment);
                    this.analyticService.trackEvent(AnalyticEvents.EventsFilterByAway);
                    break;

                case 'newHire':
                    await this.eventService.loadNewHireEvents(this.selectedDepartment);
                    this.analyticService.trackEvent(AnalyticEvents.EventsFilterByFirstDay);
                    break;

                default:
                    await this.eventService.loadAllEvents(this.selectedDepartment);
                    this.analyticService.trackEvent(AnalyticEvents.EventsFilterByAll);
                    break;
            }
            this.days = this.eventService.getDays();
            this.firstTwoDays = this.eventService.getFirstTwoDays();
            this.next7DaysDateGrouping = this.eventService.getNext7Days();
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    onDepartmentFilter(departmentId: string | number): void {
        if (typeof departmentId === 'number') {
            this.eventService.setFilterOptions(departmentId);
            this.selectedDepartment = departmentId;
        } else {
            this.selectedDepartment = null;
            this.eventService.setFilterOptions(null);
        }

        this.getSelectedEvents(this.selectedEvent);
    }

    onCalendarClick(): void {
        this.analyticService.trackEvent(AnalyticEvents.EventsViewCalendar);
        this.router.navigate(['/calendar', 'company', 'time-off']);
    }

    private async getDepartments(): Promise<void> {
        const [departments] = await Department.param('company', this.auth.company.id).orderBy('name', 'ASC').all();
        this.allDepartments = departments;
    }
}
